import { useState, useEffect } from 'react';
import { formatDate, formatDateRange } from '../utils/formatDate';
import { FileText, Plus, Loader2, DollarSign, Calendar, CheckCircle2, Clock, FileEdit, XCircle, Sparkles, Search, LayoutGrid, List, Users, Trash2, Settings, type LucideIcon } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { SORT_OPTIONS, STATUS_OPTIONS, canDeleteSow, defaultView, deleteSowPrompt, filterSows, peopleOptions, progressOf, sortSows, sowPeople, sowValue, type FirmUserLite, type PersonFilter, type SowSort, type SowView } from '../utils/sowListView';
import { defaultMineOnly } from '../utils/clientListFilters';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { SOWGenerator } from './SOWGenerator';
import { Alert, AlertDescription } from './ui/alert';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { projectId } from '../utils/supabase/info';
import { toast } from 'sonner';
import { formatCategoryLabel } from '../utils/normalizeAcronyms';
import { storedFixedBidQuote } from '../utils/fixedBidMarkup';
import { formatDollars, markupNote } from '../utils/fixedBidMarkupText';

interface SOWListProps {
  accessToken: string;
  onViewSOW: (sowId: string) => void;
  onCreateSOW: () => void;
  currentUser?: any;
  onNavigateToSettings?: () => void;
}

interface SOW {
  id: string;
  companyId: string;
  companyName: string;
  consultingFirmId: string;
  name: string;
  description: string;
  status: 'draft' | 'pending_approval' | 'approved' | 'active' | 'completed' | 'rejected';
  deliverables: Array<{
    id: string;
    name: string;
    description: string;
    dueDate: string;
    status: 'pending' | 'in_progress' | 'completed';
  }>;
  pricing: {
    type: 'fixed' | 'hourly' | 'hybrid';
    amount: number;
    hourlyRate?: number;
    estimatedHours?: number;
    /** A fixed bid's markup: `amount` is the bid total. */
    basePrice?: number;
    markupPercent?: number;
    markupAmount?: number;
  };
  timeline: {
    startDate: string;
    endDate: string;
    milestones: Array<{
      id: string;
      name: string;
      date: string;
      status: 'pending' | 'completed';
    }>;
  };
  createdAt: string;
  createdBy: string;
  updatedAt?: string;
  approvedAt?: string;
  approvedBy?: string;
  ownerId?: string;
  teamMembers?: string[];
  assignedConsultants?: string[];
}

const VIEW_KEY = 'pathkit.sows.view';

const STATUS_CONFIG: Record<string, { variant: "default" | "secondary" | "destructive" | "outline", className: string, icon: LucideIcon }> = {
  draft: { variant: 'outline', className: 'bg-gray-50 text-gray-700 border-gray-200', icon: FileEdit },
  pending_approval: { variant: 'outline', className: 'bg-yellow-50 text-yellow-700 border-yellow-200', icon: Clock },
  approved: { variant: 'outline', className: 'bg-green-50 text-green-700 border-green-200', icon: CheckCircle2 },
  active: { variant: 'outline', className: 'bg-blue-50 text-blue-700 border-blue-200', icon: FileText },
  completed: { variant: 'outline', className: 'bg-purple-50 text-purple-700 border-purple-200', icon: CheckCircle2 },
  rejected: { variant: 'outline', className: 'bg-red-50 text-red-700 border-red-200', icon: XCircle },
  archived: { variant: 'outline', className: 'bg-gray-100 text-gray-500 border-gray-200', icon: FileText },
};

const UNKNOWN_CONFIG: { variant: "outline", className: string, icon: LucideIcon } = { variant: 'outline', className: 'bg-gray-100 text-gray-500 border-gray-200', icon: FileText };

export function SOWList({ accessToken, onViewSOW, onCreateSOW, currentUser, onNavigateToSettings }: SOWListProps) {
  const [sows, setSOWs] = useState<SOW[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  // BD reps start on their own SOWs, as the old list did (RESTORE_PLAN R3.5)
  const [personFilter, setPersonFilter] = useState<PersonFilter>(() => (defaultMineOnly(currentUser) ? 'me' : 'everyone'));
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [sort, setSort] = useState<SowSort>('updated');
  const [view, setView] = useState<SowView | null>(null);
  const [firmUsers, setFirmUsers] = useState<FirmUserLite[]>([]);
  const [sowGeneratorOpen, setSOWGeneratorOpen] = useState(false);
  const [clientSelectOpen, setClientSelectOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState<any>(null);
  const [companies, setCompanies] = useState<any[]>([]);
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [companySearch, setCompanySearch] = useState('');
  const [engagementTypesCount, setEngagementTypesCount] = useState<number | null>(null);
  const [checkingEngagementTypes, setCheckingEngagementTypes] = useState(true);

  useEffect(() => {
    loadSOWs();
    checkEngagementTypes();
    loadFirmUsers();
  }, []);

  // The firm's people, so a lead or team member id reads as a name in the list and the people filter.
  const loadFirmUsers = async () => {
    const firmId = currentUser?.firmId || currentUser?.consultingFirmId;
    if (!firmId) return;
    try {
      const response = await fetch(`https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/firms/${encodeURIComponent(firmId)}/users`, { headers: { 'Authorization': `Bearer ${accessToken}` } });
      if (!response.ok) return;
      const body = await response.json();
      setFirmUsers(Array.isArray(body.users) ? body.users : []);
    } catch { /* names fall back to ids */ }
  };

  // Cards for a few SOWs, a table for a page of them; the viewer's choice is remembered.
  const chooseView = (next: SowView) => {
    setView(next);
    try { localStorage.setItem(VIEW_KEY, next); } catch { /* per-viewer convenience only */ }
  };
  useEffect(() => {
    if (loading || view !== null) return;
    let saved: string | null = null;
    try { saved = localStorage.getItem(VIEW_KEY); } catch { saved = null; }
    setView(saved === 'cards' || saved === 'table' ? saved : defaultView(sows.length));
  }, [loading, sows.length, view]);

  // Auto-migrate legacy 'new' status OR 'Untitled SOW' names
  useEffect(() => {
    const hasLegacySows = sows.some(s => s.status === 'new' || s.name === 'Untitled SOW' || !s.name);
    if (hasLegacySows && accessToken) {
      const migrate = async () => {
        try {
          console.log('[SOWList] Detected legacy data. Triggering auto-fix...');
          const response = await fetch(`https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/maintenance/migrate-new-sows`, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${accessToken}` }
          });
          
          if (response.ok) {
            const result = await response.json();
            if (result.migrated > 0) {
              toast.success(`Fixed ${result.migrated} SOWs`);
              loadSOWs(); // Reload to get clean data
            }
          } else {
             console.warn('[SOWList] Migration failed with status:', response.status);
          }
        } catch (e) {
          console.error("[SOWList] Migration failed", e);
        }
      };
      migrate();
    }
  }, [sows, accessToken]);

  const checkEngagementTypes = async () => {
    if (!currentUser?.firmId && !currentUser?.consultingFirmId) {
      setCheckingEngagementTypes(false);
      return;
    }

    try {
      const firmId = currentUser.firmId || currentUser.consultingFirmId;
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/firms/${firmId}/engagement-types`,
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setEngagementTypesCount(data.engagementTypes?.length || 0);
      }
    } catch (error) {
      console.error('Error checking engagement types:', error);
    } finally {
      setCheckingEngagementTypes(false);
    }
  };

  const loadSOWs = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/consultant/sows`,
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        console.log('[SOW LIST] Loaded SOWs:', data.sows?.length || 0);
        console.log('[SOW LIST] First few SOWs:', data.sows?.slice(0, 3));
        setSOWs(data.sows || []);
      } else {
        toast.error('Failed to load SOWs');
      }
    } catch (error) {
      console.error('Error loading SOWs:', error);
      toast.error('Failed to load SOWs');
    } finally {
      setLoading(false);
    }
  };

  // Delete from the list (the old SOWsList's trash button): creator, firm admin or system admin
  const deleteSOW = async (sow: SOW) => {
    if (!window.confirm(deleteSowPrompt(sow))) return;
    setDeletingId(sow.id);
    try {
      const response = await fetch(`https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/consultant/sows/${encodeURIComponent(sow.id)}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${accessToken}` },
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(body.error || 'Could not delete the SOW');
      setSOWs((list) => list.filter((s) => s.id !== sow.id));
      toast.success('SOW deleted');
    } catch (e: any) {
      toast.error(e?.message || 'Could not delete the SOW');
    } finally {
      setDeletingId(null);
    }
  };

  // The badges the old list carried: from an intake, and the configuration items in scope
  const sowBadges = (sow: SOW) => (
    <>
      {(sow as any).originIntakeId && <Badge variant="secondary" className="bg-blue-100 text-blue-700" data-testid="sow-from-intake">From intake</Badge>}
      {Number((sow as any).configItemCount) > 0 && (
        <Badge variant="outline" data-testid="sow-config-count"><Settings className="size-3 mr-1" aria-hidden="true" />{(sow as any).configItemCount} config {Number((sow as any).configItemCount) === 1 ? 'item' : 'items'}</Badge>
      )}
    </>
  );

  const getStatusBadge = (status: SOW['status'] | string, sowId?: string) => {
    // PROMPT A: Enhanced Normalization (trim, lower, spaces/hyphens -> underscores)
    let normalized = (status ?? '').toString().trim().toLowerCase()
      .replace(/[\s-]/g, '_')     // spaces/hyphens -> underscores
      .replace(/_+/g, '_')        // collapse multiple underscores
      .replace(/^_+|_+$/g, '');   // trim leading/trailing underscores
    
    // Auto-map legacy 'new' to 'draft' for display purposes (until migration completes)
    if (normalized === 'new') {
        normalized = 'draft';
    }

    // PROMPT C: Safer Lookup (hasOwnProperty)
    const isKnownStatus = Object.prototype.hasOwnProperty.call(STATUS_CONFIG, normalized);
    const config = isKnownStatus ? STATUS_CONFIG[normalized] : UNKNOWN_CONFIG;

    // PROMPT D: Refined Telemetry (log only if unknown AND non-empty)
    if (!isKnownStatus && normalized) {
      console.warn(`[SOWList] Unknown status`, { sowId, rawStatus: status, normalized });
    }

    const Icon = config.icon;
    
    let label = '';
    if (isKnownStatus) {
        label = formatCategoryLabel(normalized);
    } else {
        label = normalized ? `Unknown (${normalized})` : 'Unknown';
    }

    return (
      <Badge variant={config.variant} className={config.className}>
        <Icon className="size-3 mr-1" />
        {label}
      </Badge>
    );
  };

  const getPricingDisplay = (pricing: SOW['pricing']) => {
    // Handle missing or incomplete pricing data
    if (!pricing || !pricing.type) {
      return 'Pricing TBD';
    }
    
    if (pricing.type === 'fixed') {
      return `$${(pricing.amount || 0).toLocaleString()} Fixed${markupNote(storedFixedBidQuote(pricing))}`;
    } else if (pricing.type === 'hourly') {
      return `$${pricing.hourlyRate || 0}/hr (${pricing.estimatedHours || 0}h est.)`;
    } else {
      return `$${(pricing.amount || 0).toLocaleString()} + $${pricing.hourlyRate || 0}/hr`;
    }
  };

  const getProgressPercentage = (sow: SOW) => {
    if (!sow.deliverables || sow.deliverables.length === 0) return 0;
    const completed = sow.deliverables.filter(d => d.status === 'completed').length;
    return Math.round((completed / sow.deliverables.length) * 100);
  };

  const people = peopleOptions(sows, firmUsers);
  const filteredSOWs = sortSows(filterSows(sows, { search: searchTerm, status: filterStatus, person: personFilter }, { users: firmUsers, viewer: currentUser }), sort) as SOW[];
  const activeView: SowView = view ?? defaultView(sows.length);

  const handleGenerateSOW = () => {
    if (engagementTypesCount === 0) {
      toast.error('Please set up engagement types first');
      if (onNavigateToSettings) {
        onNavigateToSettings();
      }
    } else {
      setSOWGeneratorOpen(true);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="py-12 flex items-center justify-center">
          <Loader2 className="size-8 animate-spin text-gray-400" />
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Onboarding Alert for Engagement Types */}
      {!checkingEngagementTypes && engagementTypesCount === 0 && (
        <Alert className="border-purple-200 bg-purple-50">
          <Sparkles className="size-4 text-purple-600" />
          <AlertDescription className="flex items-center justify-between">
            <div className="flex-1">
              <p className="font-medium text-purple-900 mb-1">
                Set up Engagement Types to unlock SOW Generation
              </p>
              <p className="text-sm text-purple-700">
                Before generating branded SOWs, create reusable engagement type templates (e.g., "Cloud Migration", "Security Assessment") with standard deliverables, timelines, and pricing.
              </p>
            </div>
            <Button
              onClick={() => {
                if (onNavigateToSettings) {
                  onNavigateToSettings();
                } else {
                  toast.info('Navigate to Settings → Engagement Catalog to create engagement types');
                }
              }}
              className="ml-4 bg-purple-600 hover:bg-purple-700"
            >
              Set Up Now
            </Button>
          </AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <FileText className="size-5 text-blue-600" />
                Statements of Work
                <Badge variant="secondary">{sows.length}</Badge>
              </CardTitle>
              <CardDescription>
                Manage SOWs across all client engagements
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Button 
                onClick={handleGenerateSOW}
                className="gap-2 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700"
                disabled={checkingEngagementTypes}
              >
                <Sparkles className="size-4" />
                Generate SOW
              </Button>
              <Button onClick={onCreateSOW} variant="outline" className="gap-2">
                <Plus className="size-4" />
                Add SOW
              </Button>
            </div>
          </div>

          {/* Search and filters: one row; status and people are selects, the view toggles cards and table */}
          <div className="flex gap-2 mt-4 flex-wrap items-center" data-testid="sows-toolbar">
            <div className="relative">
              <Search className="size-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
              <Input
                placeholder="Search client, SOW, lead or team"
                aria-label="Search SOWs"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-8 w-64"
              />
            </div>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-44" aria-label="Status"><SelectValue /></SelectTrigger>
              <SelectContent>
                {STATUS_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={personFilter} onValueChange={(v) => setPersonFilter(v as PersonFilter)}>
              <SelectTrigger className="w-48" aria-label="People"><Users className="size-4 mr-1 text-muted-foreground" aria-hidden="true" /><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="everyone">Everyone's SOWs</SelectItem>
                <SelectItem value="me">My SOWs</SelectItem>
                {people.map((name) => <SelectItem key={name} value={name}>{name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={sort} onValueChange={(v) => setSort(v as SowSort)}>
              <SelectTrigger className="w-48" aria-label="Sort"><SelectValue /></SelectTrigger>
              <SelectContent>
                {SORT_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <div className="ml-auto flex items-center gap-1" role="group" aria-label="View">
              <Button variant={activeView === 'cards' ? 'default' : 'outline'} size="sm" aria-pressed={activeView === 'cards'} aria-label="Cards" onClick={() => chooseView('cards')}><LayoutGrid className="size-4" /></Button>
              <Button variant={activeView === 'table' ? 'default' : 'outline'} size="sm" aria-pressed={activeView === 'table'} aria-label="Table" onClick={() => chooseView('table')}><List className="size-4" /></Button>
            </div>
          </div>
          {(filteredSOWs.length !== sows.length) && (
            <p className="text-xs text-muted-foreground mt-2">{filteredSOWs.length} of {sows.length} SOWs</p>
          )}
        </CardHeader>

        <CardContent>
          {filteredSOWs.length === 0 ? (
            <div className="py-12 text-center text-muted-foreground">
              <FileText className="size-12 mx-auto mb-3 text-gray-400" />
              <p className="font-medium">No SOWs found</p>
              <p className="text-sm">Create your first SOW to get started</p>
              <Button onClick={onCreateSOW} className="mt-4 gap-2">
                <Plus className="size-4" />
                Create SOW
              </Button>
            </div>
          ) : activeView === 'table' ? (
            <div className="overflow-x-auto" data-testid="sows-table">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Client</TableHead>
                    <TableHead>SOW</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Lead</TableHead>
                    <TableHead>Team</TableHead>
                    <TableHead className="text-right">Value</TableHead>
                    <TableHead>Dates</TableHead>
                    <TableHead className="text-right">Progress</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredSOWs.map((sow) => {
                    const { leadName, team } = sowPeople(sow, firmUsers);
                    const value = sowValue(sow);
                    const markup = storedFixedBidQuote(sow.pricing);
                    return (
                      <TableRow key={sow.id} className="cursor-pointer" onClick={() => onViewSOW(sow.id)}>
                        <TableCell className="font-medium whitespace-nowrap">{sow.companyName}</TableCell>
                        <TableCell className="max-w-[18rem] truncate">{(!sow.name || sow.name === 'Untitled SOW') ? `Draft SOW${formatDate(sow.createdAt) ? ` - ${formatDate(sow.createdAt)}` : ''}` : sow.name}</TableCell>
                        <TableCell><div className="flex flex-wrap items-center gap-1">{getStatusBadge(sow.status, sow.id)}{sowBadges(sow)}</div></TableCell>
                        <TableCell className="whitespace-nowrap">{leadName ?? <span className="text-muted-foreground">—</span>}</TableCell>
                        <TableCell className="max-w-[14rem] truncate" title={team.join(', ')}>{team.length ? team.join(', ') : <span className="text-muted-foreground">—</span>}</TableCell>
                        <TableCell className="text-right whitespace-nowrap tabular-nums" title={markupNote(markup).trim() || undefined}>
                          {value ? `$${value.toLocaleString()}` : <span className="text-muted-foreground">—</span>}
                          {value > 0 && markup && <span className="block text-xs text-muted-foreground" data-testid="sow-table-markup">base {formatDollars(markup.base)} + {markup.percent}%</span>}
                        </TableCell>
                        <TableCell className="whitespace-nowrap text-muted-foreground">{formatDateRange(sow.timeline?.startDate, sow.timeline?.endDate) || '—'}</TableCell>
                        <TableCell className="text-right tabular-nums">{(sow.status === 'active' || sow.status === 'completed') ? `${progressOf(sow)}%` : <span className="text-muted-foreground">—</span>}</TableCell>
                        <TableCell className="text-right whitespace-nowrap">
                          <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); onViewSOW(sow.id); }}>Open</Button>
                          {canDeleteSow(sow, currentUser) && (
                            <Button variant="ghost" size="sm" aria-label={`Delete ${sow.name || 'SOW'}`} disabled={deletingId === sow.id} onClick={(e) => { e.stopPropagation(); void deleteSOW(sow); }}>
                              <Trash2 className="size-4 text-destructive" aria-hidden="true" />
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredSOWs.map(sow => {
                const progress = getProgressPercentage(sow);
                return (
                  <Card
                    key={sow.id}
                    className="hover:bg-gray-50 transition-colors cursor-pointer"
                    onClick={() => onViewSOW(sow.id)}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-2">
                            <h3 className="font-bold text-lg truncate">{sow.companyName}</h3>
                            {getStatusBadge(sow.status, sow.id)}
                            {sowBadges(sow)}
                          </div>
                          <p className="text-sm text-muted-foreground mb-2 line-clamp-1">
                            {(!sow.name || sow.name === 'Untitled SOW') ? `Draft SOW${formatDate(sow.createdAt) ? ` - ${formatDate(sow.createdAt)}` : ''}` : sow.name}
                          </p>
                          <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
                            {sow.description}
                          </p>
                          
                          <div className="flex items-center gap-4 flex-wrap text-sm">
                            <div className="flex items-center gap-1 text-muted-foreground">
                              <DollarSign className="size-4" />
                              {getPricingDisplay(sow.pricing)}
                            </div>
                            {formatDateRange(sow.timeline?.startDate, sow.timeline?.endDate) && (
                              <div className="flex items-center gap-1 text-muted-foreground">
                                <Calendar className="size-4" />
                                {formatDateRange(sow.timeline?.startDate, sow.timeline?.endDate)}
                              </div>
                            )}
                            <div className="flex items-center gap-1 text-muted-foreground">
                              <FileText className="size-4" />
                              {sow.deliverables?.length || 0} deliverables
                            </div>
                          </div>

                          {(sow.status === 'active' || sow.status === 'completed') && (
                            <div className="mt-3">
                              <div className="flex items-center justify-between text-sm mb-1">
                                <span className="text-muted-foreground">Progress</span>
                                <span className="font-medium">{progress}%</span>
                              </div>
                              <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                                <div
                                  className="h-full bg-blue-600 transition-all"
                                  style={{ width: `${progress}%` }}
                                />
                              </div>
                            </div>
                          )}
                        </div>
                        <div className="flex flex-col items-end gap-2">
                          <Button variant="outline" size="sm" onClick={(e) => {
                            e.stopPropagation();
                            onViewSOW(sow.id);
                          }}>
                            View Details
                          </Button>
                          {canDeleteSow(sow, currentUser) && (
                            <Button variant="ghost" size="sm" aria-label={`Delete ${sow.name || 'SOW'}`} disabled={deletingId === sow.id} onClick={(e) => { e.stopPropagation(); void deleteSOW(sow); }}>
                              <Trash2 className="size-4 text-destructive" aria-hidden="true" />
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* SOW Generator Dialog */}
      {sowGeneratorOpen && currentUser && (
        <SOWGenerator
          open={sowGeneratorOpen}
          onOpenChange={setSOWGeneratorOpen}
          accessToken={accessToken}
          consultingFirm={{
            id: currentUser.firmId || currentUser.consultingFirmId,
            name: currentUser.firmName || 'Your Firm',
            defaultHourlyRate: 150,
          } as any}
          onNavigateToEngagementCatalog={() => {
            setSOWGeneratorOpen(false);
            if (onNavigateToSettings) {
              onNavigateToSettings();
            }
          }}
        />
      )}
    </div>
  );
}